import React, { useState, useEffect, useMemo } from "react";
import { Popper, Paper, ClickAwayListener } from '@mui/material';
import { Box } from '@mui/joy';
import { useTranslation } from 'react-i18next';
import DateFilter from "src/mtska-frontend-app-component/components/List/customSelectionFilter/DateFilter/DateFilter";
import { InputFilterMultiCheckbox } from 'src/mtska-frontend-app-component';
import { useSettingContextInComponent, useSettingsContext } from "src/mtska-frontend-app-scaffolding";
import FiltersPanel from "src/mtska-frontend-app-component/components/List/FiltersPanel";
import { getColumnDefinition } from 'src/mtska-frontend-app-component/components/List/list.functions.jsx'
import { useDataProvider } from "src/mtska-frontend-data-provider";
import JSONPretty from 'react-json-pretty';

const AGENT_EMPLOYMENT_FILTERS = {
    items: [
        { field: 'role', operator: 'equals', value: 'Agent' },
        { field: 'terminated_at_readable', operator: 'isEmpty', value: '_' },
    ],
};

 const Filters = ({ dataProviderName, filters: defaultFilters, style }) => {
    const { t } = useTranslation();
    const { filterModel, drawable } = useSettingContextInComponent(dataProviderName, defaultFilters);
    const { settings, settingsLoaded, addFilter } = useSettingsContext(dataProviderName, defaultFilters);
    const [filterPanelVisible, setFilterPanelVisible] = useState(false);
    const { item: config } = useDataProvider('configs/config');

    const [customDateRange, setCustomDateRange] = useState();
    const [areaValue, setAreaValue] = useState(null);
    const [shopValue, setShopValue] = useState(null);
    const [agentValue, setAgentValue] = useState(null);
    const [sourceValue, setSourceValue] = useState(null);
    const [statusValue, setStatusValue] = useState(null);
    const [productValue, setProductValue] = useState(null);

    const leadStatusStaticOptions = useMemo(
        () => [
            { status: 'new', label: t('new') },
            { status: 'existing', label: t('existing') },
        ],
        [t],
    );

    const leadProductStaticOptions = useMemo(
        () => [
            { product: 'buy', label: t('buy') },
            { product: 'sell', label: t('sell') },
            { product: 'rent_in', label: t('rent_in') },
            { product: 'lease_out', label: t('lease_out') },
            { product: 'referral_out', label: t('referral_out') },
        ],
        [t],
    );

    useEffect(() => {
        if (settingsLoaded === false) return;

        setCustomDateRange(filterModel?.items?.find(f => f.field === 'registered_at')?.value || null);

        const areaItem = filterModel?.items?.find(f => f.field === '_related_area_name');
        const areaRaw = areaItem?.value;
        if (areaRaw == null || areaRaw === '') {
            setAreaValue(null);
        } else {
            const names =
                areaItem.operator === 'in' && Array.isArray(areaRaw) ? areaRaw : [areaRaw];
            const list = names.filter((n) => n != null && n !== '');
            setAreaValue(list.length ? { name: list } : null);
        }

        const shopItem = filterModel?.items?.find(f => f.field === '_related_shop_name');
        const shopRaw = shopItem?.value;
        if (shopRaw == null || shopRaw === '') {
            setShopValue(null);
        } else {
            const names =
                shopItem.operator === 'in' && Array.isArray(shopRaw) ? shopRaw : [shopRaw];
            const list = names.filter((n) => n != null && n !== '');
            setShopValue(list.length ? { name: list } : null);
        }

        const agentItem = filterModel?.items?.find(f => f.field === '_related_agent_fullname');
        const agentRaw = agentItem?.value;
        if (agentRaw == null || agentRaw === '') {
            setAgentValue(null);
        } else {
            const names =
                agentItem.operator === 'in' && Array.isArray(agentRaw) ? agentRaw : [agentRaw];
            const list = names.filter((n) => n != null && n !== '');
            setAgentValue(list.length ? { _related_person_fullname: list } : null);
        }

        const sourceItem = filterModel?.items?.find(f => f.field === '_related_source_name');
        const sourceRaw = sourceItem?.value;
        if (sourceRaw == null || sourceRaw === '') {
            setSourceValue(null);
        } else {
            const names =
                sourceItem.operator === 'in' && Array.isArray(sourceRaw) ? sourceRaw : [sourceRaw];
            const list = names.filter((n) => n != null && n !== '');
            setSourceValue(list.length ? { name: list } : null);
        }

        const statusItem = filterModel?.items?.find(f => f.field === 'status');
        const statusRaw = statusItem?.value;
        if (statusRaw == null || statusRaw === '') {
            setStatusValue(null);
        } else {
            const names =
                statusItem.operator === 'in' && Array.isArray(statusRaw) ? statusRaw : [statusRaw];
            const list = names.filter((n) => n != null && n !== '');
            setStatusValue(list.length ? { status: list } : null);
        }

        const productItem = filterModel?.items?.find(f => f.field === 'product');
        const productRaw = productItem?.value;
        if (productRaw == null || productRaw === '') {
            setProductValue(null);
        } else {
            const names =
                productItem.operator === 'in' && Array.isArray(productRaw) ? productRaw : [productRaw];
            const list = names.filter((n) => n != null && n !== '');
            setProductValue(list.length ? { product: list } : null);
        }
    }, [filterModel, settingsLoaded]);

    return (drawable &&
        <>
             {/* filterModel in Filters
            <JSONPretty data={filterModel} className="json-wrapper"></JSONPretty> */}

            <Box className="section-filters" sx={style}>
                <div>
                    <DateFilter
                        applyValue={(updatedItem) => {
                            addFilter('registered_at', updatedItem.value, 'between')
                        }}
                        item={{
                            field: 'registered_at',
                            operator: 'between',
                            value: customDateRange,
                        }}
                        label="registered_at"
                    />
                </div>

                <InputFilterMultiCheckbox
                    dataProvider="commercials/area"
                    valueKey="name"
                    value={areaValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_area_name', null);
                        } else {
                            const names = Array.isArray(v.name) ? v.name : [v.name];
                            addFilter('_related_area_name', names, 'in');
                        }
                    }}
                    label={t('area')}
                    labelkey="name"
                    project={['name']}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    dataProvider="commercials/shop"
                    valueKey="name"
                    value={shopValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_shop_name', null);
                        } else {
                            const names = Array.isArray(v.name) ? v.name : [v.name];
                            addFilter('_related_shop_name', names, 'in');
                        }
                    }}
                    label={t('shop_name')}
                    labelkey="name"
                    project={['name']}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    dataProvider="persons/employment"
                    valueKey="_related_person_fullname"
                    labelkey="_related_person_fullname"
                    value={agentValue}
                    filters={AGENT_EMPLOYMENT_FILTERS}
                    project={['_related_person_fullname']}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_agent_fullname', null);
                        } else {
                            const names = Array.isArray(v._related_person_fullname)
                                ? v._related_person_fullname
                                : [v._related_person_fullname];
                            addFilter('_related_agent_fullname', names, 'in');
                        }
                    }}
                    label={t('agent')}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    dataProvider="advertisings/source"
                    valueKey="name"
                    value={sourceValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('_related_source_name', null);
                        } else {
                            const names = Array.isArray(v.name) ? v.name : [v.name];
                            addFilter('_related_source_name', names, 'in');
                        }
                    }}
                    label={t('source')}
                    labelkey="name"
                    project={['name']}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    staticOptions={leadStatusStaticOptions}
                    valueKey="status"
                    labelkey="label"
                    value={statusValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('status', null);
                        } else {
                            const names = Array.isArray(v.status) ? v.status : [v.status];
                            addFilter('status', names, 'in');
                        }
                    }}
                    label={t('status')}
                    style={{ flex: '1' }}
                    size="small"
                />

                <InputFilterMultiCheckbox
                    staticOptions={leadProductStaticOptions}
                    valueKey="product"
                    labelkey="label"
                    value={productValue}
                    onChange={(v) => {
                        if (v == null) {
                            addFilter('product', null);
                        } else {
                            const names = Array.isArray(v.product) ? v.product : [v.product];
                            addFilter('product', names, 'in');
                        }
                    }}
                    label={t('product')}
                    style={{ flex: '1' }}
                    size="small"
                />
                
                {/* per ora disabilitiamo il bottone di apertura dei filtri
                <Button onClick={(e) => { e.stopPropagation(); setFilterPanelVisible(!filterPanelVisible)}} icon="faEllipsisVertical"></Button> 
                */}
            </Box>

            <ClickAwayListener onClickAway={() => setFilterPanelVisible(false)}>
                <Popper open={filterPanelVisible}>
                    <Paper>
                        <FiltersPanel dataProviderName={dataProviderName} columns={getColumnDefinition(settings, config, dataProviderName, t)} />
                    </Paper>
                </Popper>
            </ClickAwayListener>
        </>
    )
}

export default Filters;